// ============================================================================
// VOICE CHAT SERVICE - WebRTC Voice Communication
// ============================================================================

export class VoiceChatService {
  private static instance: VoiceChatService | null = null;
  
  private localStream: MediaStream | null = null;
  private micEnabled = false;
  private peerConnections = new Map<string, RTCPeerConnection>();
  private audioElements = new Map<string, HTMLAudioElement>();
  
  // Team-based communication
  private allowedPeers = new Set<string>(); // Peers we can talk to

  private constructor() {}

  static getInstance(): VoiceChatService {
    if (!VoiceChatService.instance) {
      VoiceChatService.instance = new VoiceChatService();
    }
    return VoiceChatService.instance;
  }

  async initialize(): Promise<void> {
    console.log('[VoiceChat] Initialized');
  }

  async enableMicrophone(): Promise<boolean> {
    if (this.micEnabled) return true;

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      this.micEnabled = true;
      console.log('[VoiceChat] Microphone enabled');
      
      // Add tracks to existing peer connections
      this.updatePeerTracks();
      
      return true;
    } catch (e) {
      console.error('[VoiceChat] Failed to enable microphone:', e);
      return false;
    }
  }

  disableMicrophone(): void {
    if (!this.micEnabled) return;

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    
    this.micEnabled = false;
    console.log('[VoiceChat] Microphone disabled');
    
    // Remove tracks from peer connections
    this.updatePeerTracks();
  }

  async toggleMicrophone(): Promise<boolean> {
    if (this.micEnabled) {
      this.disableMicrophone();
      return false;
    } else {
      return await this.enableMicrophone();
    }
  }

  isMicrophoneEnabled(): boolean {
    return this.micEnabled;
  }

  setAllowedPeers(peerIds: string[]): void {
    this.allowedPeers.clear();
    peerIds.forEach(id => this.allowedPeers.add(id));
    console.log('[VoiceChat] Allowed peers:', peerIds);
  }

  addPeerConnection(peerId: string, pc: RTCPeerConnection): void {
    // Only add if peer is allowed (same team or 1v1)
    if (!this.allowedPeers.has(peerId)) {
      console.log('[VoiceChat] Peer not allowed:', peerId);
      return;
    }

    this.peerConnections.set(peerId, pc);
    
    // Handle incoming audio - SET THIS FIRST before any tracks are added
    pc.ontrack = (event) => {
      console.log('[VoiceChat] Received track from:', peerId, 'streams:', event.streams.length);
      if (event.streams && event.streams.length > 0) {
        const remoteStream = event.streams[0];
        
        let audio = this.audioElements.get(peerId);
        if (!audio) {
          audio = new Audio();
          audio.autoplay = true;
          audio.volume = 1.0;
          // Append to DOM to ensure playback
          audio.style.display = 'none';
          document.body.appendChild(audio);
          this.audioElements.set(peerId, audio);
          console.log('[VoiceChat] Created audio element for:', peerId);
        }
        audio.srcObject = remoteStream as any;
        
        // Try to play (handle autoplay policy)
        audio.play().catch(e => {
          console.warn('[VoiceChat] Audio play failed (autoplay policy):', e);
        });
      }
    };
    
    // Add local audio track if mic is enabled
    if (this.localStream && this.micEnabled) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('[VoiceChat] Adding local audio track to peer:', peerId);
        pc.addTrack(audioTrack, this.localStream);
      }
    }

    console.log('[VoiceChat] Added peer connection:', peerId, 'mic enabled:', this.micEnabled);
  }

  removePeerConnection(peerId: string): void {
    this.peerConnections.delete(peerId);
    
    const audio = this.audioElements.get(peerId);
    if (audio) {
      audio.srcObject = null;
      this.audioElements.delete(peerId);
    }
    
    console.log('[VoiceChat] Removed peer connection:', peerId);
  }

  private async updatePeerTracks(): Promise<void> {
    for (const [peerId, pc] of this.peerConnections) {
      // Remove existing audio tracks
      const senders = pc.getSenders();
      for (const sender of senders) {
        if (sender.track?.kind === 'audio') {
          pc.removeTrack(sender);
          console.log('[VoiceChat] Removed audio track from peer:', peerId);
        }
      }

      // Add new track if mic is enabled
      if (this.localStream && this.micEnabled) {
        const audioTrack = this.localStream.getAudioTracks()[0];
        if (audioTrack) {
          pc.addTrack(audioTrack, this.localStream);
          console.log('[VoiceChat] Added audio track to peer:', peerId);
        }
      }
    }
  }

  cleanup(): void {
    this.disableMicrophone();
    
    // Don't close peer connections - they're managed by NetworkManager
    this.peerConnections.clear();
    
    this.audioElements.forEach((audio) => {
      audio.srcObject = null;
      audio.pause();
      // Remove from DOM
      if (audio.parentNode) {
        audio.parentNode.removeChild(audio);
      }
    });
    this.audioElements.clear();
    
    this.allowedPeers.clear();
    
    console.log('[VoiceChat] Cleaned up');
  }
}
